import { Clear, CloudUploadOutlined, InsertDriveFile } from '@mui/icons-material'
import DownloadIcon from '@mui/icons-material/Download'
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography
} from '@mui/material'
import Checkbox from '@mui/material/Checkbox'
import Grid from '@mui/material/Grid'
import { ExplanationAccordion } from 'components'
import { useLayout } from 'context'
import * as controllers from 'controllers'
import { VehicleDocumentDto, VehicleDto } from 'dtos'
import { useLoadingState } from 'hooks'
import { DEBOUNCE_DELAY } from 'localConstants'
import { enqueueSnackbar } from 'notistack'
import { VehicleDocumentsParameters } from 'parameters'
import { useEffect, useState } from 'react'
import { formatDateTime, useDebounce } from 'utils'
import VehicleDocumentUploadDialog from './VehicleDocumentUploadDialog'

interface DocumentDetailsCardProps {
  vehicle: VehicleDto
}

export default function DocumentDetailsCard({ vehicle }: DocumentDetailsCardProps) {
  const layout = useLayout()

  const [vehicleDocumentUploadDialogOpen, setVehicleDocumentUploadDialogOpen] = useState<boolean>(false)
  const [selectedVehicleDocuments, setSelectedVehicleDocuments] = useState<VehicleDocumentDto[]>([])
  const [vehicleDocuments, setVehicleDocuments] = useState<VehicleDocumentDto[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)

  const [loadingState, setLoadingState] = useLoadingState({
    isGettingVehicleDocuments: false,
    isCreatingVehicleDocument: false,
    isDownloadingVehicleDocument: false,
    isDeletingVehicleDocument: false,
    isGettingDocuments: false,
    isCreatingDocument: false
  })

  const [parameters, setParameters] = useState<VehicleDocumentsParameters>({
    page: 0,
    pageSize: 10,
    orderBy: 'vehicleDocumentType',
    order: 'asc'
  })

  const getVehicleDocuments = useDebounce((vehicleGuid: string, parameters: VehicleDocumentsParameters) => {
    setLoadingState('isGettingVehicleDocuments', true)
    controllers
      .getVehicleDocuments(vehicleGuid, parameters)
      .then(response => {
        setTotalCount(response.totalCount ?? 0)
        setVehicleDocuments(response.value)
      })
      .finally(() => {
        setLoadingState('isGettingVehicleDocuments', false)
      })
  }, DEBOUNCE_DELAY)

  useEffect(() => {
    if (vehicle.vehicleGuid) {
      getVehicleDocuments(vehicle.vehicleGuid, parameters)
    }
  }, [vehicle, parameters, getVehicleDocuments])

  useEffect(() => {
    layout.setIsLayoutLoading(loadingState.isDeletingVehicleDocument || loadingState.isDownloadingVehicleDocument)
  }, [layout, loadingState.isDeletingVehicleDocument, loadingState.isDownloadingVehicleDocument])

  return (
    <>
      <VehicleDocumentUploadDialog
        initialValues={new VehicleDocumentDto()}
        vehicle={vehicle}
        isLoading={loadingState.isCreatingVehicleDocument}
        open={vehicleDocumentUploadDialogOpen}
        onClose={() => setVehicleDocumentUploadDialogOpen(false)}
        onSave={values => {
          setLoadingState('isCreatingVehicleDocument', true)
          controllers
            .createVehicleDocument(vehicle.vehicleGuid!, values)
            .then(res => {
              enqueueSnackbar('Vehicle Document Saved Successfully!', { variant: 'success' })
              getVehicleDocuments(vehicle.vehicleGuid, parameters)
              setVehicleDocumentUploadDialogOpen(false)
            })
            .finally(() => setLoadingState('isCreatingVehicleDocument', false))
        }}
      />

      <Card variant='outlined'>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm='auto'>
                <Typography fontWeight='bold' variant='h5'>
                  Documents
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto' container spacing={1}>
                <Grid item>
                  <Button
                    onClick={() => {
                      setVehicleDocumentUploadDialogOpen(true)
                    }}
                    color='primary'
                    endIcon={<CloudUploadOutlined />}
                    variant='text'
                  >
                    UPLOAD
                  </Button>
                </Grid>

                <Grid item>
                  <Button
                    color='primary'
                    disabled={selectedVehicleDocuments.length === 0}
                    startIcon={<DownloadIcon />}
                    onClick={() => {
                      setLoadingState('isDownloadingVehicleDocument', true)
                      selectedVehicleDocuments.forEach(svd => {
                        controllers.getVehicleDocumentFile(vehicle.vehicleGuid!, svd.documentGuid!, svd.displayFileName!)
                      })
                      setLoadingState('isDownloadingVehicleDocument', false)
                    }}
                    variant='text'
                  >
                    DOWNLOAD
                  </Button>
                </Grid>

                <Grid item>
                  <Button
                    color='error'
                    disabled={selectedVehicleDocuments.length === 0}
                    endIcon={<Clear />}
                    onClick={() => {
                      setLoadingState('isDeletingVehicleDocument', true)
                      controllers
                        .deleteVehicleDocuments(vehicle.vehicleGuid!, selectedVehicleDocuments)
                        .then(() => {
                          enqueueSnackbar('Vehicle Documents Deleted Successfully!', { variant: 'success' })
                          getVehicleDocuments(vehicle.vehicleGuid, parameters)
                          setSelectedVehicleDocuments([])
                        })
                        .finally(() => setLoadingState('isDeletingVehicleDocument', false))
                    }}
                    variant='text'
                  >
                    DELETE
                  </Button>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {loadingState.isGettingVehicleDocuments && (
              <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
                <Grid>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            {!loadingState.isGettingVehicleDocuments && (
              <Grid item xs={12}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell padding='checkbox'>
                          <Checkbox
                            color='primary'
                            checked={selectedVehicleDocuments.length === totalCount}
                            indeterminate={selectedVehicleDocuments.length > 0 && selectedVehicleDocuments.length !== totalCount}
                            onChange={() => {
                              if (selectedVehicleDocuments.length === totalCount) {
                                setSelectedVehicleDocuments([])
                              } /* if (selectedVehicleDocuments.length > 0) */ else {
                                setSelectedVehicleDocuments(vehicleDocuments)
                              }
                            }}
                          />
                        </TableCell>

                        <TableCell
                          sx={theme => ({ color: theme.palette.primary.main })}
                          sortDirection={parameters.orderBy === 'documentType' ? parameters.order : false}
                        >
                          <TableSortLabel
                            active={parameters.orderBy === 'documentType'}
                            direction={parameters.orderBy === 'documentType' ? parameters.order : undefined}
                            onClick={() => {
                              setParameters({
                                ...parameters,
                                order: parameters.orderBy === 'documentType' && parameters.order === 'asc' ? 'desc' : 'asc',
                                orderBy: 'documentType'
                              })
                            }}
                          >
                            File Type
                          </TableSortLabel>
                        </TableCell>

                        <TableCell
                          sx={theme => ({ color: theme.palette.primary.main })}
                          sortDirection={parameters.orderBy === 'displayFileName' ? parameters.order : false}
                        >
                          <TableSortLabel
                            active={parameters.orderBy === 'displayFileName'}
                            direction={parameters.orderBy === 'displayFileName' ? parameters.order : undefined}
                            onClick={() => {
                              setParameters({
                                ...parameters,
                                order: parameters.orderBy === 'displayFileName' && parameters.order === 'asc' ? 'desc' : 'asc',
                                orderBy: 'displayFileName'
                              })
                            }}
                          >
                            File Name
                          </TableSortLabel>
                        </TableCell>

                        <TableCell
                          sx={theme => ({ color: theme.palette.primary.main })}
                          sortDirection={parameters.orderBy === 'createdDateTime' ? parameters.order : false}
                        >
                          <TableSortLabel
                            active={parameters.orderBy === 'createdDateTime'}
                            direction={parameters.orderBy === 'createdDateTime' ? parameters.order : undefined}
                            onClick={() => {
                              setParameters({
                                ...parameters,
                                order: parameters.orderBy === 'createdDateTime' && parameters.order === 'asc' ? 'desc' : 'asc',
                                orderBy: 'createdDateTime'
                              })
                            }}
                          >
                            Modified
                          </TableSortLabel>
                        </TableCell>

                        <TableCell
                          sx={theme => ({ color: theme.palette.primary.main })}
                          sortDirection={parameters.orderBy === 'createdBy' ? parameters.order : false}
                        >
                          <TableSortLabel
                            active={parameters.orderBy === 'createdBy'}
                            direction={parameters.orderBy === 'createdBy' ? parameters.order : undefined}
                            onClick={() => {
                              setParameters({
                                ...parameters,
                                order: parameters.orderBy === 'createdBy' && parameters.order === 'asc' ? 'desc' : 'asc',
                                orderBy: 'createdBy'
                              })
                            }}
                          >
                            Uploaded
                          </TableSortLabel>
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {vehicleDocuments?.map((vehicleDocument, index) => (
                        <TableRow key={index} sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}>
                          <TableCell padding='checkbox'>
                            <Checkbox
                              color='primary'
                              checked={selectedVehicleDocuments.some(svd => svd.vehicleDocumentGuid === vehicleDocument.vehicleDocumentGuid)}
                              onChange={() => {
                                setSelectedVehicleDocuments(prev =>
                                  selectedVehicleDocuments.some(svd => svd.vehicleDocumentGuid === vehicleDocument.vehicleDocumentGuid)
                                    ? prev.filter(svd => svd.vehicleDocumentGuid !== vehicleDocument.vehicleDocumentGuid)
                                    : [...prev, vehicleDocument]
                                )
                              }}
                            />
                          </TableCell>

                          <TableCell>
                            <InsertDriveFile color='primary' sx={{ verticalAlign: 'middle', marginRight: '8px' }} />

                            {vehicleDocument.documentType?.name}
                          </TableCell>

                          <TableCell>{vehicleDocument.displayFileName}</TableCell>

                          <TableCell>{formatDateTime(vehicleDocument.createdDateTime!)}</TableCell>

                          <TableCell>
                            {vehicleDocument.createdBy?.firstName} {vehicleDocument.createdBy?.lastName}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Grid item xs={12}>
                  <TablePagination
                    component='div'
                    count={totalCount}
                    onPageChange={(_, page: number) => {
                      setParameters({
                        ...parameters,
                        page
                      })
                    }}
                    onRowsPerPageChange={e => {
                      setParameters({
                        ...parameters,
                        page: 0,
                        pageSize: e.target.value as unknown as number
                      })
                    }}
                    page={parameters.page}
                    rowsPerPage={parameters.pageSize}
                    labelRowsPerPage='Rows per page:'
                    rowsPerPageOptions={[
                      { label: '5', value: 5 },
                      { label: '10', value: 10 },
                      { label: '25', value: 25 },
                      { label: '50', value: 50 },
                      { label: '100', value: 100 }
                    ]}
                    SelectProps={{ fullWidth: false }}
                  />
                </Grid>
              </Grid>
            )}

            <Grid item xs={12}>
              <ExplanationAccordion variant='outlined'>
                {`||
Documents pertaining to or referencing the vehicle are displayed on this screen. The “File Type” column reflects how the file is
used by VTS2. File names are displayed as “(VIN) - (File Type)” for system-generated documents or as “uploaded file name” for
uploaded or OCR'd files. Selecting a file's row will allow users to download the selected document to the user's machine or
delete the document if that document is able to be deleted based on vehicle status.
||
For images related to a vehicle's check-in such as a Check-In Odometer image, those images are available under the Check-In
tab. Images related to the Conformity process are available under the Conformity tab.
||
Pressing “Upload” will open a file browsing window for selecting files from the user's machine. Any documents uploaded will be
given the file type label of “Misc Document”. These files will not be OCR'd, parsed, or machine read and are simply for keeping
vehicle-related documents organized. Allowed file types are .pdf, .docx, .doc, .png, .jpeg, .jpg, and .bmp with a maximum allowed
file size of 20 MB.
||
If users delete an uploaded document that had “Does this file type have an associated charge” = “Yes”, the associated line item
is deleted from the transactions table on the “Transactions” card under Vehicle Details – Invoicing (tab) and the Amount
associated with the uploaded document is deducted from the selected “Charge Type” on the “Invoicing” card under Vehicle
Details – Invoicing (tab).
||
The Abrams Auto Transport Invoice is emailed to BWI from Abrams Auto Transport, the company that is handling the Inbound
Trips truck driving for BWI. Abrams updated their Invoice to have the PAPS # vehicles are associated with appear in the “PO#”
field on the Invoice document and this PAPS # is used to connect the Abrams Auto Transport Invoice to the PAPS and then to
the vehicles associated with that PAPS.
||
The 7501 is an OCR’d document. It is emailed by the Broker (BCB International) to the Registered Importer (BWI) as a PDF email
attachment. The 7501 document contains the Broker Invoice #, the PAPS #, and the VINs within that PAPS.
||
The Bond is generated by VTS2 after the vehicle is locked, added to a PAPS within an Inbound Trip, and then that PAPS is
locked. The Bond document is included in “To Broker PDF” sent to the Broker and Transporter Company prior to the Inbound Trip
and is also included in the ConformityShipper.zip which is uploaded to the DOT Portal during the Conformity process.
||
The Bond Release is an OCR'd document. This document is provided by the DOT, but a user must manually sign into the DOT
portal once this document becomes available, download it as a PDF, and then drop that PDF into the OCR feature for it to be
parsed which associates it with the vehicle in VTS2. When the Bond Release document is associated with a vehicle, that vehicle
is immediately able to be added to an Outbound Trip.
||
The CAN Ownership Papers are an OCR'd document. This document is provided by the Exporter Company, but does not always
come over with the vehicle within the Inbound Trip and arrives in the mail after Vehicle Check-In. The OCR'd copy of the CAN
Ownership Papers allows the Registered Importer to verify possession of these documents, but it is the original copy of the CAN
Ownership Papers that are required to be used in the Title Application process.
||
The EPA-3520 is generated by VTS2 after the vehicle is locked, added to a PAPS within an Inbound Trip, and then that PAPS is
locked. The EPA-3520 is included in “To Broker PDF” sent to the Broker and Transporter Company prior to the Inbound Trip and
is also included in the ConformityShipper.zip which is uploaded to the DOT Portal during the Conformity process.
||
The HS-7 Declaration is generated by VTS2 after the vehicle is locked, added to a PAPS within an Inbound Trip, and then that
PAPS is locked. The HS-7 Declaration is included in “To Broker PDF” sent to the Broker and Transporter Company prior to the
Inbound Trip and is also included in the ConformityShipper.zip which is uploaded to the DOT Portal during the Conformity
process.
||
The TR-34 - Certification (Cluster Conversion aka Michigan Department of State Certification) is generated by VTS2 during the
Michigan Title Application process and is submitted to the SoS for Michigan Title application. This document not scanned back
into VTS2 after being printed and signed. This document is included in the Michigan Title Application packet, even when Cluster
Conversion = No.
||
The RD-108 - Michigan Title Application is generated by VTS2 during the Michigan Title Application process and is submitted to
the SoS for Michigan Title application. This document is not scanned back into VTS2 after being printed and signed.
||
The TR-114 - Special Mailing of Certificate Title is generated by VTS2 during the Michigan Title Application process and is
submitted to the SoS for Michigan Title application. This document is not scanned back into VTS2 after being printed and
signed. The Special Mailer tells the Michigan Secretary of State what address the new Title should be mailed to.
||
The Michigan Title Application Receipt is an OCR'd document. This document is mailed to the Registered Importer a few days
after a Registered Importer member drops off the Michigan Title Application Packet at a Michigan Secretary of State branch as
part of the Michigan title application process.
||
The Michigan Title is an OCR'd document. This is the actual new title provided by the Michigan Secretary of State after the
Michigan Title Application process is completed which puts the vehicle into the Register Importer Company name or the name
of the Exporter or Buyer company associated with the vehicle. Vehicles with a “Title Application State” of Michigan will have this
document, except for vehicles where “Registered Importer Will Not Receive Title” was checked in the Michigan Titles feature
(this checkbox is used alongside the “TR-114 Special Mailing of Certificate Title” to send the new Michigan Title directly to the
Exporter or Buyer instead of BWI receiving and OCR’ing the new Michigan Title).
||
The BMV 3774 - Ohio Application For Certificate of Title to a Motor Vehicle is generated by VTS2 and is utilized for applying for a
vehicle title in Ohio. This file is not scanned back into VTS2 or OCR’d after it has been printed and signed. It collects essential
information such as applicant details (name, address, contact), vehicle specifics (make, model, VIN, odometer reading), and title
specifics (type of title, liens). The applicant must sign and date the form, which may require notarization, and include applicable
fees. This form is crucial for legally establishing vehicle ownership in compliance with Ohio's regulations, ensuring accurate title
registration with the Ohio Bureau of Motor Vehicles (BMV). This form is part of the Ohio Title Application packet which is
submitted to the Ohio BMV.
||
The BMV 4334 - Ohio Out of State Motor Vehicle Physical Inspection is a mandatory document required for registering a vehicle
in Ohio if it was previously titled in another state or country. The inspection ensures that the vehicle's identification number (VIN)
matches the documentation and verifies basic vehicle information, such as the make, model, body type, and odometer reading.
Conducted by an authorized inspector, such as a deputy registrar or licensed motor vehicle dealer, the form must be completed
and submitted to the Ohio Bureau of Motor Vehicles as part of the registration process. This form is part of the Ohio Title
Application packet which is submitted to the Ohio BMV. This file is not scanned back into VTS2 or OCR’d after it has been
printed and signed.
||
The BMV 3724 - Ohio Odometer Disclosure Statement contains the name and address of both the Buyer and Seller, information
about the vehicle (VIN, Year, Make and Model) as well as the vehicle mileage at time of sale. This form is part of the Ohio Title
Application packet which is submitted to the Ohio BMV. This file is not scanned back into VTS2 or OCR’d after it has been
printed and signed.
||
The Vehicle Purchase Agreement is a required document that contains the name and address of both the Buyer and Seller,
information about the vehicle (VIN, Year, Make and Model) as well as the vehicle mileage at time of sale. This form is part of the
Ohio Title Application packet which is submitted to the Ohio BMV. This file is not scanned back into VTS2 or OCR’d after it has
been printed and signed.
||
The Ohio Title Application Receipt is provided by the Ohio BMV along with the new Ohio Title. This document does not have OCR
support in VTS2 currently.
||
The Ohio Title is an OCR'd document. This is the actual new title provided by the Ohio Bureau of Motor Vehicle after the Ohio
Title Application process is completed which puts the vehicle into the Register Importer Company name or the name of the
Exporter or Buyer company associated with the vehicle.
||
The Recall Documentation OEM Site Screenshot shows if there are any open recalls on the vehicle or not. This screenshot is
uploaded as a PDF when a user creates a Recall Campaign with “No Recall” or when a creates a Recall Campaign with “Recall”,
sets the repaid status to “Remedy”, and then uploads this screenshot as a PDF. Note that users will upload both a “Canadian
OEM Site Screenshot” and “USA OEM Site Screenshot” for each vehicle record.
||
The Recall Repair Order (RO) is a document from the company or dealership that did the repair/recall work. This “receipt” is
required when users select a Recall Campaign Repair Status of “Repaired.” This document is referenced during the vehicle
Invoicing process.
||
The Signed Foreign Shipper is the printed, signed, scanned, and uploaded copy of the Foreign Shipper document generated by
VTS2 for each PAPS in an Inbound Trip where the PAPS contains at least one vehicle where the first character in the VIN is a 1,
4, or 5 (each vehicle meeting these criteria is listed on the Foreign Shipper document for that PAPS). If a Foreign Shippers
document was generated for the PAPS, the signed copy must be uploaded to the PAPS before the Inbound Trip can be locked.
Signed Foreign Shippers upload supports these file types: PDF, PNG, JPG, JPEG. The Signed Foreign Shipper is not an OCR’d
document.
||
The Statement of Conformity is generated by VTS2 when users press the “Create Files To Sign” button below the “Ready To
Print” table in the Conformity feature. Each vehicle has its own Statement of Conformity and the document contains information
about the Registered Importer, Vehicle Information, information relating to the Inbound Trip the vehicle is associated with, as
well as a matrix of radio buttons indicating that the vehicle’s conformity to the FMVSS and other applicable requirements have
been accomplished (radio button options include “Conformed at time of original manufacturer”, “Modified to conform after
original manufacture” or “Not Applicable”). There is a barcode on the bottom of the Statement of Conformity representing the
VIN, and this barcode is leveraged when OCR’ing the Signed Statement of Conformity. This document is generated by VTS2,
printed, signed, scanned to PDF, and is then OCR’d and associated with the VIN as the Signed Statement of Conformity.
||
The Signed Statement of Conformity is literally a signed copy of the Statement of Conformity for this vehicle. The Statement of
Conformity is generated by VTS2 within the Conformity feature. There is a barcode on the bottom of the Statement of
Conformity representing the VIN, and this barcode is leveraged when OCR’ing the Signed Statement of Conformity. This
document is generated by VTS2, printed, signed, scanned to PDF, and is then OCR’d and associated with the VIN as the Signed
Statement of Conformity. The Signed Statement of Conformity is included in the Conformity PDF which appears within the
vehicle’s Conformity ZIP that gets uploaded to the DOT Portal during the Conformity process.
||
The Signed Outbound Bill of Lading (BOL) is the copy of the Outbound Trip’s Outbound Bill of Lading that has been signed by the
Outbound Transport Company’s driver. This is an OCR’d document that has a barcode on it that VTS2 generates. After the
Signed BOL has been OCR’d, it is associated with the Outbound Trip and then also with the vehicles in that Outbound Trip (the
OCR’d Signed BOL is available under Outbound Trip Details and under the Vehicle Details – Documents (tab) for all vehicles in
the Outbound Trip).
||
The Warranty Certificate is generated by VTS2 when users press “Create Files To Sign” on the Conformity screen. This
document is effectively the warranty document BWI provides to the Exporter or Buyer of the vehicle by placing the warranty
document in the vehicle’s glove box. There is one vehicle per Warranty Certificate. This document is titled “Vehicle Compliance
Services Corporation. Warranty Certificate of Financial Responsibility of Registered Importer To Remedy Safety Related Defects
and Noncompliances”.
`}
              </ExplanationAccordion>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
